/**
 * Reactive read + one-time hydration of the pipeline recency store (SE-5489).
 *
 * Wraps {@link recentPipelineBoardIdsVar} so consumers get a reactive,
 * most-recent-first list of recently-selected pipeline board ids, and triggers
 * a single AsyncStorage rehydration on mount. Hydration is fire-and-forget: the
 * empty default renders a clean chip row until (and if) persisted ids load.
 * @module features/player-kanban/hooks/usePipelineRecency
 */

import { useReactiveVar } from "@apollo/client";
import { useEffect } from "react";

import {
  hydrateRecentPipelines,
  recentPipelineBoardIdsVar,
} from "@/features/player-kanban/stores/pipelineRecencyStore";

/**
 * Returns the reactive most-recent-first list of pipeline board ids and kicks
 * off a one-time rehydration from AsyncStorage.
 * @returns The current recency list (most recent first).
 */
export const usePipelineRecency = (): readonly string[] => {
  const recent = useReactiveVar(recentPipelineBoardIdsVar);

  // One-time rehydration from persisted storage. Not a setState-in-effect —
  // the store owns its var; this only asks it to load once per session.
  useEffect(() => {
    void hydrateRecentPipelines();
  }, []);

  return recent;
};
